import { useRef, useState } from "react";
import { api } from "@/lib/api";
import { Upload, FileXls, CheckCircle, Warning, X, DownloadSimple } from "@phosphor-icons/react";

/**
 * CaseImportModal — CEO-only NFOR Excel/CSV bulk import with preview → confirm flow.
 * Opens via a parent button. Pass `onClose` and `onImported` callbacks.
 */
export default function CaseImportModal({ onClose, onImported }) {
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [hideDemo, setHideDemo] = useState(true);
  const [executed, setExecuted] = useState(null);

  const dlTemplate = async (fmt) => {
    try {
      const res = await api.get(`/cases/import/template.${fmt}`, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(new Blob([res.data]));
      a.download = `SPAN_NFOR_Cases_Template.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove();
    } catch (e) {
      setErr(`Template download failed: ${e?.response?.data?.detail || e.message}`);
    }
  };

  const onFileChange = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!/\.(xlsx|xlsm|csv)$/i.test(f.name)) {
      setErr("File must be .xlsx, .xlsm or .csv");
      return;
    }
    setErr(null);
    setFile(f);
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/cases/import/preview", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(data);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || e2.message || "Preview failed");
      setFile(null);
    } finally {
      setBusy(false);
    }
  };

  const confirmImport = async () => {
    if (!file || !preview) return;
    if (!window.confirm(
      `Confirm import?\n\n` +
      `• Insert: ${preview.summary.to_insert} new cases\n` +
      `• Update: ${preview.summary.to_update} existing cases\n` +
      `• Skip:   ${preview.summary.to_skip} (no changes)\n` +
      `• Invalid: ${preview.summary.invalid} rows will be ignored\n` +
      (hideDemo ? `• ${preview.summary.legacy_seed_candidates_to_hide} dummy/seed cases will be HIDDEN from Live SPAN matters.\n` : "") +
      `\nThis can be reverted via Admin → Restore Demo Data.`
    )) return;

    setBusy(true);
    setErr(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("confirmation", "CONFIRM-IMPORT-CASES");
      fd.append("hide_demo", hideDemo ? "true" : "false");
      const { data } = await api.post("/cases/import/execute", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setExecuted(data);
      if (typeof onImported === "function") onImported(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || "Import failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[200] bg-black/60 flex items-center justify-center p-4" data-testid="case-import-modal">
      <div className="bg-[var(--surface,#fff)] rounded-lg shadow-2xl w-full max-w-3xl max-h-[92vh] overflow-hidden flex flex-col">
        <div className="px-5 py-3 border-b border-[var(--border)] flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Upload size={20} weight="duotone" className="text-[var(--brand-primary)]" />
            <div>
              <h2 className="font-bold text-[var(--text-primary)]">Import NFOR cases</h2>
              <div className="text-[11px] text-[var(--text-muted)]">Excel or CSV · CEO-only · idempotent by Reference no.</div>
            </div>
          </div>
          <button onClick={onClose} data-testid="case-import-close"><X size={18} /></button>
        </div>

        <div className="flex-1 overflow-auto p-5 space-y-4">
          {/* STEP 1: download template */}
          <section>
            <div className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] mb-1.5">Step 1 — Download a blank template</div>
            <div className="flex gap-2 flex-wrap">
              <button onClick={() => dlTemplate("xlsx")} data-testid="dl-template-xlsx"
                className="span-btn span-btn-ghost h-8 text-[12px] inline-flex items-center gap-1.5">
                <DownloadSimple size={13} /> Excel template
              </button>
              <button onClick={() => dlTemplate("csv")} data-testid="dl-template-csv"
                className="span-btn span-btn-ghost h-8 text-[12px] inline-flex items-center gap-1.5">
                <DownloadSimple size={13} /> CSV template
              </button>
              <span className="text-[11px] text-[var(--text-muted)] self-center">
                Includes README tab with field hints + a sample row.
              </span>
            </div>
          </section>

          {/* STEP 2: upload */}
          <section>
            <div className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] mb-1.5">Step 2 — Upload your filled file</div>
            <input ref={fileInputRef} type="file" accept=".xlsx,.xlsm,.csv" onChange={onFileChange}
                   data-testid="case-import-file-input" className="hidden" />
            <button onClick={() => fileInputRef.current?.click()} disabled={busy}
                    data-testid="case-import-pick-file"
                    className="span-btn h-9 text-[12px] inline-flex items-center gap-1.5 disabled:opacity-50">
              <FileXls size={14} /> {file ? `Re-pick (${file.name})` : "Pick Excel / CSV file"}
            </button>
          </section>

          {/* STEP 3: preview */}
          {busy && !preview && <div className="text-[12px] text-[var(--text-muted)]">Parsing file…</div>}
          {preview && (
            <section data-testid="case-import-preview">
              <div className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] mb-1.5">Step 3 — Preview the diff</div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
                <StatTile label="To insert" value={preview.summary.to_insert} testid="stat-insert" tone="success" />
                <StatTile label="To update" value={preview.summary.to_update} testid="stat-update" tone="info" />
                <StatTile label="Skip (no change)" value={preview.summary.to_skip} testid="stat-skip" tone="muted" />
                <StatTile label="Invalid (ignored)" value={preview.summary.invalid} testid="stat-invalid" tone="warn" />
              </div>
              {preview.warnings?.length > 0 && (
                <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2 mb-2 flex items-start gap-1.5" data-testid="case-import-warnings">
                  <Warning size={13} className="mt-0.5 shrink-0" />
                  <div>{preview.warnings.join(" · ")}</div>
                </div>
              )}
              {preview.samples?.inserts?.length > 0 && (
                <details className="mb-2 text-[12px]" data-testid="sample-inserts">
                  <summary className="cursor-pointer font-semibold text-[var(--brand-primary)]">First {preview.samples.inserts.length} of {preview.summary.to_insert} inserts</summary>
                  <ul className="mt-1 pl-4 list-disc">
                    {preview.samples.inserts.map((r, i) => (
                      <li key={i}><span className="font-mono">{r.ref || "(no ref)"}</span> — {r.client} <span className="text-[var(--text-muted)]">[{r.nature || "?"} · {r.stage || "intake"}]</span></li>
                    ))}
                  </ul>
                </details>
              )}
              {preview.samples?.invalid?.length > 0 && (
                <details className="mb-2 text-[12px]" data-testid="sample-invalid">
                  <summary className="cursor-pointer font-semibold text-amber-700">{preview.samples.invalid.length} invalid sample row(s)</summary>
                  <ul className="mt-1 pl-4 list-disc">
                    {preview.samples.invalid.map((r, i) => (
                      <li key={i}>Row {r.row}: <span className="text-amber-700">{r.reason}</span></li>
                    ))}
                  </ul>
                </details>
              )}

              <div className="mt-3 border-t border-[var(--border)] pt-3 space-y-2">
                <label className="flex items-start gap-2 text-[12px]">
                  <input type="checkbox" checked={hideDemo} onChange={e => setHideDemo(e.target.checked)}
                         data-testid="hide-demo-toggle" className="mt-0.5" />
                  <span>
                    <strong>Hide dummy/seed data</strong> after import — flags <span className="font-mono">{preview.summary.legacy_seed_candidates_to_hide}</span> non-imported cases as <span className="font-mono">is_demo=true</span> so they disappear from <em>All Cases</em> and <em>Live SPAN matters</em>. Reversible via the Restore button.
                  </span>
                </label>
              </div>
            </section>
          )}

          {executed && (
            <div className="border border-emerald-200 bg-emerald-50 text-emerald-900 rounded p-3 text-[12px]" data-testid="case-import-result">
              <div className="font-bold flex items-center gap-1.5 mb-1"><CheckCircle size={14} /> Import complete</div>
              <ul className="pl-4 list-disc">
                <li>Inserted: <strong>{executed.applied.inserted}</strong></li>
                <li>Updated: <strong>{executed.applied.updated}</strong></li>
                <li>Skipped (no change): <strong>{executed.applied.skipped}</strong></li>
                {executed.demo_hidden > 0 && <li>Dummy cases hidden: <strong>{executed.demo_hidden}</strong></li>}
              </ul>
            </div>
          )}

          {err && (
            <div className="text-[12px] text-red-800 bg-red-50 border border-red-200 px-3 py-2 rounded" data-testid="case-import-error">
              {String(err)}
            </div>
          )}
        </div>

        <div className="border-t border-[var(--border)] px-5 py-3 flex justify-end gap-2">
          <button onClick={onClose} className="span-btn span-btn-ghost">Close</button>
          {preview && !executed && (
            <button onClick={confirmImport} disabled={busy} data-testid="case-import-execute"
                    className="span-btn">
              {busy ? "Importing…" : `Confirm & import ${preview.summary.to_insert + preview.summary.to_update}`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function StatTile({ label, value, testid, tone }) {
  const toneClass = {
    success: "border-emerald-200 bg-emerald-50 text-emerald-900",
    info:    "border-sky-200 bg-sky-50 text-sky-900",
    warn:    "border-amber-200 bg-amber-50 text-amber-900",
    muted:   "border-[var(--border)] bg-[var(--surface-2)] text-[var(--text-primary)]",
  }[tone] || "";
  return (
    <div className={`border rounded p-2.5 ${toneClass}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider opacity-70">{label}</div>
      <div className="text-2xl font-bold tabular-nums leading-tight">{value}</div>
    </div>
  );
}
